import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';


/**
 * Component that handles centralized routing logic for authenticated users
 * Must be inside Router context
 */
const AuthRouteManager: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { 
    isAuthenticated, 
    isLoading, 
    availableGroups, 
    currentGroupSlug,
    user,
    switchGroup
  } = useAuth();

  // Extract group slug from current URL
  const extractGroupSlugFromUrl = () => {
    const { getFirstPathSegment, isPublicRouteSegment } = require('../../constants/routes');
    const firstSegment = getFirstPathSegment(location.pathname);
    
    // Skip known public routes
    if (isPublicRouteSegment(firstSegment)) {
      return null;
    }
    
    return firstSegment;
  };

  // Centralized routing logic
  useEffect(() => {
    const handleRouting = async () => {
      if (!isAuthenticated || isLoading || availableGroups.length === 0) {
        return;
      }

      // Don't interfere with public routes even for authenticated users
      const { isPublicRoute } = await import('../../constants/routes');
      
      if (isPublicRoute(location.pathname)) {
        return;
      }

    const urlGroupSlug = extractGroupSlugFromUrl();
    
    // If we're on a group-specific URL
    if (urlGroupSlug) {
      // Check if user has access to this group
      const hasAccess = availableGroups.some(group => group.slug === urlGroupSlug);
      
      if (!hasAccess) {
        navigate('/group-selection', { replace: true });
        return;
      }
      
      // If group context doesn't match URL, update it
      if (currentGroupSlug !== urlGroupSlug) {
        switchGroup(urlGroupSlug).catch(error => {
          console.error('Failed to switch group:', error);
          navigate('/group-selection', { replace: true });
        });
      }
    } else {
      // We're not on a group-specific URL, determine where to go
      if (availableGroups.length === 1) {
        // Single group - redirect to dashboard
        const groupSlug = availableGroups[0].slug;
        navigate(`/${groupSlug}/dashboard`, { replace: true });
      } else {
        // Multiple groups - check if we have a current group or need selection
        if (!currentGroupSlug) {
          navigate('/group-selection', { replace: true });
        }
      }
    }
    };

    handleRouting();
  }, [
    isAuthenticated,
    isLoading,
    availableGroups,
    currentGroupSlug,
    location.pathname,
    navigate,
    switchGroup,
    user
  ]);

  // This component doesn't render anything, it just manages routing
  return null;
};

export default AuthRouteManager;